import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subscription } from 'rxjs';
import { FetchEmployees } from 'src/app/employees/employees.actions';
import { EmployeeState } from 'src/app/employees/employees.state';
import ChildInvoiceLink from 'src/app/models/ChildInvoiceLink';
import { Employee } from 'src/app/models/Employee';
import LinkedEmployee from 'src/app/models/LinkedEmployee';
import { LinkedFileToObject } from 'src/app/models/LinkedFileToObject';
import { LinkedObjectsToFileWithId } from 'src/app/models/LinkedObjectsToFileWithId';
import { Timesheet } from 'src/app/models/Timesheet';
import { GlobalFilesService } from 'src/app/shared/global-files/global-files.service';
import { TimesheetState } from 'src/app/timesheets/timesheets.state';

import DefaultProjectEmployeeLink from '../../models/DefaultProjectEmployeeLink';
import Project from '../../models/Project';
import { AddDefaultLinkedEmployeeModalComponent } from '../add-default-linked-employee-modal/add-default-linked-employee-modal.component';
import { EditDefaultLinkedEmployeeModalComponent } from '../edit-default-linked-employee-modal/edit-default-linked-employee-modal.component';
import { DeleteDefaultEmployee, FetchProjectById } from '../project.actions';
import { ProjectService } from '../project.service';
import { ProjectState } from '../project.state';

type FreelancerMonthlyInvoiceOverview = {
    month: number;
    monthLabel: string;
    incoming: number;
    outgoing: number;
    margin: number;
    marginPercentage: number | null;
};

@Component({
    selector: 'app-default-linked-employees',
    templateUrl: './default-linked-employees.component.html',
    styleUrls: ['./default-linked-employees.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class DefaultLinkedEmployeesComponent implements OnInit, OnDestroy {
    private subscriptions = new Subscription();

    @Select(ProjectState.project) project$: Observable<Project>;
    @Select(TimesheetState.timesheetsForProject) timesheetsForProject$: Observable<Timesheet[]>;
    @Select(EmployeeState.Employees) employees$: Observable<Employee[]>;
    project: Project;
    timesheetsForProject: Timesheet[] = [];
    incomingInvoicesAmount = 0;
    outgoingInvoicesAmount = 0;
    private allLinkedFiles: LinkedFileToObject[] = [];
    private freelancerMonthlyOverviewByContractId: Record<string, Record<number, FreelancerMonthlyInvoiceOverview[]>> = {};
    private freelancerYearOptionsByContractId: Record<string, number[]> = {};
    private selectedYearByContractId: Record<string, number> = {};
    private employeesById: Record<string, Employee> = {};
    private invoiceAllocationByEmployeeId: Record<string, number> = {};
    private readonly monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    @Input() projectId: string;
    contractFreeText: string = '';

    displayedColumns: string[] = [
        'employees',
        'price per hour',
        'budget',
        'budgeted hours',
        'actual hours',
        'hours left',
        'effective from',
        'effective to',
        'po number',
        'ses number',
        'contract number',
        'free text',
        'client contract',
        'edit',
    ];
    dataSource: MatTableDataSource<LinkedEmployee> = new MatTableDataSource<LinkedEmployee>();
    @ViewChild(MatSort) sort: MatSort;

    constructor(
        private store: Store,
        private logger: NGXLogger,
        private modalService: NgbModal,
        private projectService: ProjectService,
        private globalFilesService: GlobalFilesService,
        private toastr: ToastrService,
    ) {}

    ngOnInit(): void {
        this.fetchData();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private fetchData() {
        this.logger.debug('Fetching project by id');
        this.store.dispatch(new FetchProjectById(this.projectId));
        this.subscriptions.add(
            this.project$.subscribe((project) => {
                this.project = project;
                this.contractFreeText = project?.contractFreeText || '';
                this.recomputeFreelancerOverviewData();
                if (project?.uuid) {
                    this.loadIncomingInvoicesAmount(project.uuid);
                }
            }),
        );
        this.subscriptions.add(
            this.timesheetsForProject$.subscribe((timesheets) => {
                this.timesheetsForProject = timesheets || [];
            }),
        );

        this.subscriptions.add(
            this.employees$.subscribe((employees) => {
                if (!employees) {
                    this.store.dispatch(new FetchEmployees());
                    return;
                }

                this.employeesById = (employees || []).reduce((acc: Record<string, Employee>, employee: Employee) => {
                    if (employee?.uuid) {
                        acc[employee.uuid] = employee;
                    }
                    return acc;
                }, {});

                this.recomputeFreelancerOverviewData();
            }),
        );
    }

    getBudgetedHours(contract: DefaultProjectEmployeeLink): number | null {
        const budget = Number(contract?.budget);
        const rate = Number(contract?.pricePerHour);

        if (!Number.isFinite(budget) || budget < 0 || !Number.isFinite(rate) || rate <= 0) {
            return null;
        }

        if (this.isInvoiceByDay()) {
            return (budget / rate) * this.getProjectHoursPerDay();
        }

        return budget / rate;
    }

    getActualHours(contract: DefaultProjectEmployeeLink): number {
        return this.getWorkedHoursInContractPeriod(contract);
    }

    getHoursLeft(contract: DefaultProjectEmployeeLink): number | null {
        const budgetedHours = this.getBudgetedHours(contract);
        if (budgetedHours == null) {
            return null;
        }

        return budgetedHours - this.getActualHours(contract);
    }

    getBudgetProgress(contract: DefaultProjectEmployeeLink): number {
        const raw = this.getBudgetProgressRaw(contract);
        return Math.max(0, Math.min(100, raw));
    }

    getBudgetProgressRaw(contract: DefaultProjectEmployeeLink): number {
        const budget = Number(contract?.budget);
        if (!Number.isFinite(budget) || budget <= 0) {
            return 0;
        }

        const actualCost = this.getActualCost(contract);
        const usage = (actualCost / budget) * 100;
        if (!Number.isFinite(usage) || usage < 0) {
            return 0;
        }

        return usage;
    }

    getActualCost(contract: DefaultProjectEmployeeLink): number {
        return this.getWorkedCost(contract);
    }

    isFreelancerContract(contract: DefaultProjectEmployeeLink): boolean {
        const employeeType = (this.employeesById?.[contract?.employeeId]?.type || '').toString().toUpperCase();
        return employeeType === 'FREELANCE' || employeeType === 'FREELANCER';
    }

    getIncomingInvoiceImpactForContract(contract: DefaultProjectEmployeeLink): number {
        return 0;
    }

    getBudgetByInvoiceProgressRaw(contract: DefaultProjectEmployeeLink): number {
        const budget = Number(contract?.budget);
        if (!Number.isFinite(budget) || budget <= 0) {
            return 0;
        }

        const usage = (this.getInvoiceAmountForContract(contract) / budget) * 100;
        if (!Number.isFinite(usage) || usage < 0) {
            return 0;
        }

        return usage;
    }

    getBudgetByInvoiceProgress(contract: DefaultProjectEmployeeLink): number {
        const raw = this.getBudgetByInvoiceProgressRaw(contract);
        return Math.max(0, Math.min(100, raw));
    }

    getFreelancerInvoiceOverview(contract: DefaultProjectEmployeeLink): string {
        const margin = this.outgoingInvoicesAmount - this.incomingInvoicesAmount;
        const marginPercentage = this.incomingInvoicesAmount > 0 ? (margin / this.incomingInvoicesAmount) * 100 : null;
        const marginLabel = marginPercentage === null ? '-' : `${marginPercentage.toFixed(2)}%`;
        return `Inkomend: € ${this.incomingInvoicesAmount.toFixed(2)} | Uitgaand: € ${this.outgoingInvoicesAmount.toFixed(2)} | Marge: € ${margin.toFixed(
            2,
        )} (${marginLabel})`;
    }

    hasFreelancerContracts(): boolean {
        return this.getFreelancerContracts().length > 0;
    }

    getFreelancerContracts(): DefaultProjectEmployeeLink[] {
        return (this.project?.defaultLinkedEmployees || []).filter((contract) => this.isFreelancerContract(contract));
    }

    getEmployeeNameForContract(contract: DefaultProjectEmployeeLink): string {
        const employee = this.employeesById?.[contract?.employeeId];
        if (!employee) {
            return contract?.employeeId || 'Freelancer';
        }
        const fullName = `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
        return fullName || employee.uuid || 'Freelancer';
    }

    getContractYearOptions(contract: DefaultProjectEmployeeLink): number[] {
        const contractId = this.getContractIdentifier(contract);
        return this.freelancerYearOptionsByContractId[contractId] || [];
    }

    getSelectedYearForContract(contract: DefaultProjectEmployeeLink): number {
        const contractId = this.getContractIdentifier(contract);
        return this.selectedYearByContractId[contractId] || new Date().getFullYear();
    }

    onContractYearChange(contract: DefaultProjectEmployeeLink, yearValue: string): void {
        const selectedYear = Number(yearValue);
        if (!Number.isFinite(selectedYear)) {
            return;
        }
        const contractId = this.getContractIdentifier(contract);
        this.selectedYearByContractId[contractId] = selectedYear;
    }

    getFreelancerMonthlyOverviewForContract(contract: DefaultProjectEmployeeLink): FreelancerMonthlyInvoiceOverview[] {
        const contractId = this.getContractIdentifier(contract);
        const selectedYear = this.getSelectedYearForContract(contract);
        return this.freelancerMonthlyOverviewByContractId[contractId]?.[selectedYear] || [];
    }

    getFreelancerYearTotals(contract: DefaultProjectEmployeeLink): { incoming: number; outgoing: number; margin: number; marginPercentage: number | null } {
        const rows = this.getFreelancerMonthlyOverviewForContract(contract);
        const incoming = rows.reduce((sum, row) => sum + Number(row?.incoming || 0), 0);
        const outgoing = rows.reduce((sum, row) => sum + Number(row?.outgoing || 0), 0);
        const margin = outgoing - incoming;
        const marginPercentage = incoming > 0 ? (margin / incoming) * 100 : null;
        return { incoming, outgoing, margin, marginPercentage };
    }

    private getWorkedCost(contract: DefaultProjectEmployeeLink): number {
        const rate = Number(contract?.pricePerHour);
        if (!Number.isFinite(rate) || rate <= 0) {
            return 0;
        }

        const workedHours = this.getWorkedHoursInContractPeriod(contract);
        if (workedHours <= 0) {
            return 0;
        }

        if (this.isInvoiceByDay()) {
            const workedDays = workedHours / this.getProjectHoursPerDay();
            return workedDays * rate;
        }

        return workedHours * rate;
    }

    getBudgetProgressTooltip(contract: DefaultProjectEmployeeLink): string {
        const budget = Number(contract?.budget || 0);
        const workedCost = this.getWorkedCost(contract);
        const actualCost = this.getActualCost(contract);
        const workedHours = this.getWorkedHoursInContractPeriod(contract);
        const workedSeconds = workedHours * 3600;
        const workedDays = workedHours / this.getProjectHoursPerDay();
        const usage = this.getBudgetProgressRaw(contract);
        const rate = Number(contract?.pricePerHour || 0);
        const from = contract?.effectiveFrom || '-';
        const to = contract?.effectiveTo || '-';

        const costFormula = this.isInvoiceByDay()
            ? `Kostberekening uren: ${workedDays.toFixed(2)} dagen x € ${rate.toFixed(2)} = € ${workedCost.toFixed(2)}`
            : `Kostberekening uren: ${workedHours.toFixed(2)} uren x € ${rate.toFixed(2)} = € ${workedCost.toFixed(2)}`;

        return [
            `Berekening: (jaarkost contract / budget) x 100`,
            `Worked seconds: ${workedSeconds.toFixed(0)} sec`,
            `Worked uren: ${workedHours.toFixed(2)} u`,
            `Worked dagen: ${workedDays.toFixed(2)} d (hours/day: ${this.getProjectHoursPerDay().toFixed(2)})`,
            costFormula,
            `Gelinkte incoming facturen: € ${this.incomingInvoicesAmount.toFixed(2)}`,
            `Gelinkte outgoing facturen: € ${this.outgoingInvoicesAmount.toFixed(2)}`,
            `Jaarkost contract (worklogs): € ${actualCost.toFixed(2)}`,
            `Budget: € ${budget.toFixed(2)}`,
            `Resultaat: ${usage.toFixed(2)}%`,
            `Range contract: ${from} t.e.m. ${to}`,
        ].join('\n');
    }

    getBudgetByInvoiceTooltip(contract: DefaultProjectEmployeeLink): string {
        const budget = Number(contract?.budget || 0);
        const outgoing = this.getInvoiceAmountForContract(contract);
        const usage = this.getBudgetByInvoiceProgressRaw(contract);

        return [
            `Berekening: (verkoopfacturen / budget) x 100`,
            `Verkoopfacturen (gelinkt aan dit contract/budget): € ${outgoing.toFixed(2)}`,
            `Budget: € ${budget.toFixed(2)}`,
            `Resultaat: ${usage.toFixed(2)}%`,
        ].join('\n');
    }

    getInvoiceAmountForContract(contract: DefaultProjectEmployeeLink): number {
        const employeeId = (contract?.employeeId || '').toString();
        if (!employeeId) {
            return 0;
        }

        const amount = Number(this.invoiceAllocationByEmployeeId[employeeId] || 0);
        if (!Number.isFinite(amount) || amount < 0) {
            return 0;
        }

        return amount;
    }

    private loadIncomingInvoicesAmount(projectUuid: string): void {
        this.globalFilesService.getAllFilesLinkedToObject(projectUuid).subscribe(
            (payload: any) => {
                const linkedFiles = payload?.linkedFiles || [];
                this.allLinkedFiles = linkedFiles;
                this.incomingInvoicesAmount = this.sumIncomingLinkedInvoices(linkedFiles);
                this.outgoingInvoicesAmount = this.sumOutgoingLinkedInvoices(linkedFiles);
                this.loadInvoiceAllocationsByContract(projectUuid, linkedFiles);
                this.recomputeFreelancerOverviewData();
            },
            () => {
                this.allLinkedFiles = [];
                this.incomingInvoicesAmount = 0;
                this.outgoingInvoicesAmount = 0;
                this.invoiceAllocationByEmployeeId = {};
                this.recomputeFreelancerOverviewData();
            },
        );
    }

    private loadInvoiceAllocationsByContract(projectUuid: string, linkedFiles: LinkedFileToObject[]): void {
        this.globalFilesService.getAllObjectsLinked().subscribe(
            (allLinkedObjects: LinkedObjectsToFileWithId[]) => {
                const linkedByFileId = (allLinkedObjects || []).reduce((acc: Record<string, LinkedObjectsToFileWithId>, item) => {
                    const key = (item?.yukiId || '').toString();
                    if (key) {
                        acc[key] = item;
                    }
                    return acc;
                }, {});

                const allocations: Record<string, number> = {};
                (linkedFiles || [])
                    .filter((file) => this.isOutgoingInvoice(file))
                    .forEach((file) => {
                        const fileId = (file?.fileId || '').toString();
                        if (!fileId) return;

                        const linkedForFile = linkedByFileId[fileId];
                        const projectLink = (linkedForFile?.linkedObjects || []).find((link) => link?.objectId === projectUuid);
                        (projectLink?.childInvoiceLinks || []).forEach((child: ChildInvoiceLink) => {
                            const targetId = (child?.objectId || '').toString();
                            if (!targetId) return;

                            const objectType = (child?.objectType || '').toLowerCase();
                            if (objectType !== 'employee') return;

                            const amount = Math.abs(Number(child?.amount || 0));
                            if (!Number.isFinite(amount) || amount <= 0) return;
                            allocations[targetId] = (allocations[targetId] || 0) + amount;
                        });
                    });

                this.invoiceAllocationByEmployeeId = allocations;
            },
            () => {
                this.invoiceAllocationByEmployeeId = {};
            },
        );
    }

    private recomputeFreelancerOverviewData(): void {
        this.freelancerMonthlyOverviewByContractId = {};
        this.freelancerYearOptionsByContractId = {};

        this.getFreelancerContracts().forEach((contract) => {
            const contractId = this.getContractIdentifier(contract);
            const availableYears = this.getAvailableYearsForContract(contract, this.allLinkedFiles);

            this.freelancerYearOptionsByContractId[contractId] = availableYears;

            const currentSelectedYear = this.selectedYearByContractId[contractId];
            if (!currentSelectedYear || !availableYears.includes(currentSelectedYear)) {
                this.selectedYearByContractId[contractId] = availableYears[0] || new Date().getFullYear();
            }

            this.freelancerMonthlyOverviewByContractId[contractId] = {};
            availableYears.forEach((year) => {
                this.freelancerMonthlyOverviewByContractId[contractId][year] = this.buildFreelancerMonthlyOverviewForContract(
                    contract,
                    year,
                    this.allLinkedFiles,
                );
            });
        });
    }

    private buildFreelancerMonthlyOverviewForContract(
        contract: DefaultProjectEmployeeLink,
        year: number,
        files: LinkedFileToObject[],
    ): FreelancerMonthlyInvoiceOverview[] {
        const monthly = Array.from({ length: 12 }, (_, index) => ({
            month: index + 1,
            monthLabel: this.monthNames[index],
            incoming: 0,
            outgoing: 0,
            margin: 0,
            marginPercentage: null,
        })) as FreelancerMonthlyInvoiceOverview[];

        (files || []).forEach((file) => {
            const invoiceDate = this.parseDateValue(file?.invoiceDate as any);
            if (!invoiceDate || invoiceDate.getFullYear() !== year) {
                return;
            }
            if (!this.isDateWithinContractRange(invoiceDate, contract)) {
                return;
            }

            const amount = Math.abs(Number(file?.price || 0));
            if (!Number.isFinite(amount) || amount <= 0) {
                return;
            }

            const row = monthly[invoiceDate.getMonth()];
            if (!row) {
                return;
            }

            if (this.isIncomingInvoice(file)) {
                row.incoming += amount;
            } else if (this.isOutgoingInvoice(file)) {
                row.outgoing += amount;
            }
        });

        monthly.forEach((row) => {
            row.margin = row.outgoing - row.incoming;
            row.marginPercentage = row.incoming > 0 ? (row.margin / row.incoming) * 100 : null;
        });

        return monthly;
    }

    private getAvailableYearsForContract(contract: DefaultProjectEmployeeLink, files: LinkedFileToObject[]): number[] {
        const currentYear = new Date().getFullYear();
        const fromDate = this.parseIsoDate(contract?.effectiveFrom);
        const toDate = this.parseIsoDate(contract?.effectiveTo);

        const years = new Set<number>();
        years.add(currentYear);

        if (fromDate && toDate) {
            const startYear = Math.min(fromDate.getFullYear(), toDate.getFullYear());
            const endYear = Math.max(fromDate.getFullYear(), toDate.getFullYear());
            for (let year = startYear; year <= endYear; year++) {
                years.add(year);
            }
        } else if (fromDate) {
            years.add(fromDate.getFullYear());
        } else if (toDate) {
            years.add(toDate.getFullYear());
        }

        (files || []).forEach((file) => {
            const invoiceDate = this.parseDateValue(file?.invoiceDate as any);
            if (!invoiceDate) {
                return;
            }
            if (!this.isDateWithinContractRange(invoiceDate, contract)) {
                return;
            }
            years.add(invoiceDate.getFullYear());
        });

        return Array.from(years).sort((a, b) => b - a);
    }

    private isDateWithinContractRange(date: Date, contract: DefaultProjectEmployeeLink): boolean {
        const fromDate = this.parseIsoDate(contract?.effectiveFrom);
        const toDate = this.parseIsoDate(contract?.effectiveTo);

        if (fromDate && date < fromDate) {
            return false;
        }
        if (toDate && date > toDate) {
            return false;
        }
        return true;
    }

    private getContractIdentifier(contract: DefaultProjectEmployeeLink): string {
        const uuid = (contract?.uuid || '').toString().trim();
        if (uuid) {
            return uuid;
        }

        return [
            contract?.employeeId || 'employee',
            contract?.effectiveFrom || 'from',
            contract?.effectiveTo || 'to',
            contract?.pricePerHour || 0,
            contract?.budget || 0,
        ].join('|');
    }

    private sumIncomingLinkedInvoices(files: LinkedFileToObject[]): number {
        return (files || []).filter((file) => this.isIncomingInvoice(file)).reduce((sum, file) => sum + Math.abs(Number(file?.price || 0)), 0);
    }

    private sumOutgoingLinkedInvoices(files: LinkedFileToObject[]): number {
        return (files || []).filter((file) => this.isOutgoingInvoice(file)).reduce((sum, file) => sum + Math.abs(Number(file?.price || 0)), 0);
    }

    private isIncomingInvoice(file: LinkedFileToObject): boolean {
        const type = (file?.invoiceType || '').toLowerCase();
        const description = (file?.description || '').toLowerCase();
        const title = (file?.title || '').toLowerCase();
        const isArchivedOrDeleted =
            type.includes('archived') ||
            type.includes('deleted') ||
            description.includes('archived') ||
            description.includes('deleted') ||
            title.includes('archived') ||
            title.includes('deleted');

        return file?.folderId === 1 && !isArchivedOrDeleted;
    }

    private isOutgoingInvoice(file: LinkedFileToObject): boolean {
        const type = (file?.invoiceType || '').toLowerCase();
        const description = (file?.description || '').toLowerCase();
        const title = (file?.title || '').toLowerCase();
        const isArchivedOrDeleted =
            type.includes('archived') ||
            type.includes('deleted') ||
            description.includes('archived') ||
            description.includes('deleted') ||
            title.includes('archived') ||
            title.includes('deleted');

        return file?.folderId === 2 && !isArchivedOrDeleted;
    }

    private getWorkedHoursInContractPeriod(contract: DefaultProjectEmployeeLink): number {
        if (!contract?.employeeId || !this.projectId) {
            return 0;
        }

        const fromDate = this.parseIsoDate(contract?.effectiveFrom);
        const toDate = this.parseIsoDate(contract?.effectiveTo);

        return (this.timesheetsForProject || []).reduce((totalHours, timesheet) => {
            if (timesheet?.employee?.uuid !== contract.employeeId) {
                return totalHours;
            }

            const timesheetDate = this.parseDateValue(timesheet?.expirationDate as any);
            if (!timesheetDate) {
                return totalHours;
            }
            if (fromDate && timesheetDate < fromDate) {
                return totalHours;
            }
            if (toDate && timesheetDate > toDate) {
                return totalHours;
            }

            const prorationFactor = this.getTimesheetProrationFactor(timesheetDate, fromDate, toDate);
            if (prorationFactor <= 0) {
                return totalHours;
            }

            const workedMinutes = (timesheet?.timeslots || []).reduce((minutes, slot) => {
                if (slot?.project?.uuid !== this.projectId) {
                    return minutes;
                }
                const value = Number(slot?.minutes || 0);
                return minutes + (Number.isFinite(value) && value > 0 ? value : 0);
            }, 0);

            return totalHours + (workedMinutes * prorationFactor) / 60;
        }, 0);
    }

    private getTimesheetProrationFactor(timesheetDate: Date, fromDate: Date | null, toDate: Date | null): number {
        const monthStart = new Date(timesheetDate.getFullYear(), timesheetDate.getMonth(), 1);
        const monthEnd = new Date(timesheetDate.getFullYear(), timesheetDate.getMonth() + 1, 0);

        const effectiveStart = fromDate && fromDate > monthStart ? fromDate : monthStart;
        const effectiveEnd = toDate && toDate < monthEnd ? toDate : monthEnd;

        if (effectiveEnd < effectiveStart) {
            return 0;
        }

        const totalDaysInMonth = monthEnd.getDate();
        if (totalDaysInMonth <= 0) {
            return 0;
        }

        const overlapDays = effectiveEnd.getDate() - effectiveStart.getDate() + 1;
        if (overlapDays <= 0) {
            return 0;
        }

        return Math.min(1, overlapDays / totalDaysInMonth);
    }

    private getProjectHoursPerDay(): number {
        const hoursPerDay = Number(this.project?.hoursPerDay);
        if (!Number.isFinite(hoursPerDay) || hoursPerDay <= 0) {
            return 8;
        }
        return hoursPerDay;
    }

    private isInvoiceByDay(): boolean {
        return !!this.project?.invoiceByDay;
    }

    private parseIsoDate(value?: string | null): Date | null {
        if (!value) {
            return null;
        }

        const isoPart = value.trim().split('T')[0];
        const parts = isoPart.split('-').map((part) => Number(part));
        if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
            return null;
        }

        const parsed = new Date(parts[0], parts[1] - 1, parts[2]);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }

        return parsed;
    }

    private parseDateValue(value: string | Date | null | undefined): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            if (Number.isNaN(value.getTime())) {
                return null;
            }
            return new Date(value.getFullYear(), value.getMonth(), value.getDate());
        }

        const raw = String(value).trim();
        const isoPart = raw.split('T')[0];
        const parts = isoPart.split('-').map((part) => Number(part));
        if (parts.length === 3 && parts.every((part) => Number.isFinite(part) && !Number.isNaN(part))) {
            return new Date(parts[0], parts[1] - 1, parts[2]);
        }

        const parsed = new Date(raw);
        if (Number.isNaN(parsed.getTime())) {
            return null;
        }

        return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }

    openAddDefaultEmployee() {
        const modalRef = this.modalService.open(AddDefaultLinkedEmployeeModalComponent, {
            windowClass: 'modal-prompt',
            size: 'xl',
            animation: false,
        });
        modalRef.componentInstance.project = this.project;
    }

    deleteDefaultEmployee(employee: DefaultProjectEmployeeLink) {
        this.subscriptions.add(this.store.dispatch(new DeleteDefaultEmployee(employee.uuid)).subscribe());
    }

    openEditDefaultEmployee(linkedEmployee: LinkedEmployee) {
        const modalRef = this.modalService.open(EditDefaultLinkedEmployeeModalComponent, {
            windowClass: 'modal-prompt',
            size: 'xl',
            animation: false,
        });
        modalRef.componentInstance.linkedEmployee = linkedEmployee;
        modalRef.closed.subscribe(() => this.fetchData());
    }

    saveProjectContractFreeText(): void {
        if (!this.project?.uuid) return;
        const payload: Project = {
            ...this.project,
            contractFreeText: this.contractFreeText,
        };
        this.projectService.updateProject(this.project.uuid, payload).subscribe({
            next: (updated) => {
                this.project = updated;
                this.contractFreeText = updated?.contractFreeText || '';
                this.toastr.success('Project contract text saved');
            },
            error: () => {
                this.toastr.error('Saving project contract text failed');
            },
        });
    }
}
